import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import { Progress, Spin } from 'antd';
import { apiV2ElectricMapDataTransformerPost } from '@maxtropy/device-customer-apis-v2';
import { keepOnePercentOrLine, keepOneOrLine, keepTwoOrLine } from '@/utils/utils';
import { Empty } from '@maxtropy/components';
import { memo, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import { DoubleRightOutlined } from '@ant-design/icons';
import classNames from 'classnames';

interface TotalChangeProps {
  activeId?: number;
  setActiveId?: (val?: number) => void;
  isCompactMode?: boolean;
  isNoMapLayout?: boolean;
  onCardClick?: () => void;
}

const TotalChange: React.FC<TotalChangeProps> = ({
  activeId,
  setActiveId,
  isCompactMode,
  isNoMapLayout,
  onCardClick,
}) => {
  const { data, loading } = useRequest(() =>
    apiV2ElectricMapDataTransformerPost({}).then(res => {
      setActiveId?.(res?.list?.[0]?.id);
      return res.list ?? [];
    })
  );

  const handleClick = (id?: number) => {
    setActiveId?.(id);
  };

  return (
    <Spin spinning={loading}>
      <div
        className={classNames(
          styles.container,
          isCompactMode && styles.compactContainer,
          isNoMapLayout && styles.noMapContainer
        )}
      >
        {!data?.length && <Empty description="暂无数据" />}
        {data?.map(item => (
          <div
            key={item.id}
            className={classNames(
              styles.transformerItem,
              isCompactMode && styles.compactTransformerItem,
              isNoMapLayout && styles.noMapTransformerItem
            )}
            style={{ cursor: 'pointer' }}
            onClick={() => {
              handleClick(item.id);
              if (isCompactMode) {
                onCardClick?.();
              }
            }}
          >
            <div className={classNames(styles.title, isNoMapLayout && styles.noMapTitle)}>
              <div>{item.name}</div>
              <DoubleRightOutlined
                onClick={e => {
                  e.stopPropagation();
                  window.open(`${window.DMEPLATFORMSORIGIN}/dmes/statistics/circuit?id=${item.id}`, '_blank');
                }}
                style={{ position: 'absolute', right: 16, top: 10, color: '#4A90E2' }}
              />
            </div>
            <div
              className={classNames(
                activeId === item.id ? styles.activeContent : styles.content,
                isCompactMode && styles.compactContent,
                isNoMapLayout && styles.noMapContent
              )}
            >
              {isCompactMode ? (
                <>
                  {/* 紧凑模式：实时功率、负载率、功率因数水平一行排列 */}
                  <div className={styles.compactCardRow}>
                    <div className={styles.compactCard}>
                      <div className={styles.index}>实时功率</div>
                      <div className={styles.value}>{keepOneOrLine(item.power)}kW</div>
                    </div>
                    <div className={styles.compactLoadCard}>
                      <Progress
                        type="circle"
                        size={34}
                        showInfo={false}
                        percent={(item.load ?? 0) * 100}
                        strokeColor="#5BFF60"
                        trailColor="rgba(74,144,226,0.2)"
                      />
                      <div>
                        <div className={styles.index}>负载率</div>
                        <div className={styles.value}>{keepOnePercentOrLine(item.load)}%</div>
                      </div>
                    </div>
                    <div className={styles.compactCard}>
                      <div className={styles.index}>功率因数</div>
                      <div className={styles.value}>{keepTwoOrLine(item.powerFactor)}</div>
                    </div>
                  </div>
                  {/* 今日峰值独占一行 */}
                  <div className={styles.compactPeakRow}>
                    <div className={styles.compactPeakCard}>
                      <div className={styles.peakRow}>
                        <div className={styles.peakLabel}>
                          <span>今日峰值：</span>
                          <span className={styles.time}>{item.time ? dayjs(item.time).format('HH:mm:ss') : '--'}</span>
                        </div>
                        <div className={styles.value}>{keepOneOrLine(item.summitPower)}kW</div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className={classNames(styles.cardList, isNoMapLayout && styles.noMapCardList)}>
                  <div className={classNames(styles.card, isNoMapLayout && styles.noMapMetricCard)}>
                    <div className={styles.index}>实时功率</div>
                    <div className={styles.value}>{keepOneOrLine(item.power)}kW</div>
                  </div>
                  <div className={classNames(styles.card, styles.peakCard, isNoMapLayout && styles.noMapMetricCard)}>
                    <div className={styles.peakRow}>
                      <div className={styles.peakLabel}>
                        <span>{isNoMapLayout ? '今日峰值' : '今日峰值：'}</span>
                        <span className={styles.time}>{item.time ? dayjs(item.time).format('HH:mm:ss') : '--'}</span>
                      </div>
                      <div className={styles.value}>{keepOneOrLine(item.summitPower)}kW</div>
                    </div>
                  </div>
                  <div className={classNames(styles.loadCard, isNoMapLayout && styles.noMapLoadCard)}>
                    <Progress
                      type="circle"
                      size={isNoMapLayout ? 46 : 34}
                      showInfo={false}
                      percent={(item.load ?? 0) * 100}
                      strokeColor="#5BFF60"
                      trailColor="rgba(74,144,226,0.2)"
                    />
                    <div>
                      <div className={styles.index}>负载率</div>
                      <div className={styles.value}>{keepOnePercentOrLine(item.load)}%</div>
                    </div>
                  </div>
                  <div className={classNames(styles.card, isNoMapLayout && styles.noMapMetricCard)}>
                    <div className={styles.index}>功率因数</div>
                    <div className={styles.value}>{keepTwoOrLine(item.powerFactor)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Spin>
  );
};

export default memo(TotalChange);
